// SingleEventEdit.js
// Streamlined event editing for Single Event Test flows
import * as React from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Card,
  CardContent,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack,
  ExpandMore,
  ExpandLess,
  Add as AddIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useNavigate, useBlocker } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  createTestEventWithScenariosAsync,
  selectCreateTestEventProgress,
  createAttributesOnExistingScenariosAsync,
} from '../../redux/flows/createEventSlice';
import {
  updateTestEventAsync,
  selectUpdateTestEventProgress,
  setProgress,
} from '../../redux/flows/updateEventSlice';
import { fetchFlowWithDetailsAsync } from '../../redux/flows/getFlowsSlice';
import { deleteAttributeAsync } from '../../redux/flows/deleteAttributeSlice';
import { deleteScenarioAsync } from '../../redux/flows/deleteScenarioSlice';
import { updateTestFlowAsync } from '../../redux/flows/updateFlowSlice';
import SaveProgressDialog from '../shared/SaveProgressDialog';

// Service imports
import { fetchAllFlows, filterFlowsByMarket } from '../../services/flowApiService';
import { selectMarket } from '../../redux/app/configSlice';
import { generateTempScenarioId, generateTempAttributeId } from '../../utilities/tempIdGenerator';
import { categorizeEventChanges, hasChanges } from '../../utilities/flow/categorizeChanges';
import { SCENARIO_TYPES } from '../../utilities/consts';

export default function SingleEventEdit({ flowId, initialFlowData }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [currentEvent, setCurrentEvent] = React.useState({
    event_id: null,
    event_name: '',
    event_order: 1,
    scenarios: [],
    isNew: false,
  });

  const [originalEvent, setOriginalEvent] = React.useState(null);
  const [expandedScenarios, setExpandedScenarios] = React.useState({});
  const [addScenarioModalOpen, setAddScenarioModalOpen] = React.useState(false);
  const [newScenarioType, setNewScenarioType] = React.useState({
    scenario_name: '',
    scenario_type: 'inbound',
    cloneFromInboundId: '',
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = React.useState(false);
  const [pendingNavigation, setPendingNavigation] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [showSpinner, setShowSpinner] = React.useState(false);
  const [contentReady, setContentReady] = React.useState(false);
  const [contentVisible, setContentVisible] = React.useState(false);
  const [isDiscarding, setIsDiscarding] = React.useState(false);
  const [showProgressDialog, setShowProgressDialog] = React.useState(false);
  const createProgress = useSelector(selectCreateTestEventProgress);
  const updateProgress = useSelector(selectUpdateTestEventProgress);
  const saveProgress = currentEvent?.event_db_id ? updateProgress : createProgress;

  // Flow-level state management
  const currentMarket = useSelector(selectMarket);
  const [flowData, setFlowData] = React.useState({
    flow_name: 'Single Event',
    flow_description: '',
    test_case_id: '',
  });
  const [originalFlowData, setOriginalFlowData] = React.useState(null);
  const [currentMarketFlows, setCurrentMarketFlows] = React.useState([]);
  const [testCaseIdError, setTestCaseIdError] = React.useState('');

  // Validation error state for whitespace checks
  const [validationError, setValidationError] = React.useState('');
  const [errorLocations, setErrorLocations] = React.useState({
    attribute: {}, // { attributeId: { field: 'name' | 'value' } }
    scenario: {}, // { scenarioId: true }
  });

  // Block navigation when there are unsaved changes
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname,
  );

  // Handle the blocked navigation
  React.useEffect(() => {
    if (blocker.state === 'blocked') {
      setShowUnsavedDialog(true);
      setPendingNavigation(() => () => {
        blocker.proceed();
      });
    }
  }, [blocker]);

  // Handle market changes
  React.useEffect(() => {
    const handleMarketChange = () => {
      if (hasUnsavedChanges) {
        setShowUnsavedDialog(true);
        setPendingNavigation(() => () => {
          navigate('/flows');
        });
      } else {
        navigate('/flows');
      }
    };

    window.addEventListener('marketChanged', handleMarketChange);
    return () => {
      window.removeEventListener('marketChanged', handleMarketChange);
    };
  }, [hasUnsavedChanges, navigate]);

  // Control spinner visibility with delay
  React.useEffect(() => {
    let timer;
    if (isLoading) {
      timer = setTimeout(() => setShowSpinner(true), 300);
    } else {
      setShowSpinner(false);
    }
    return () => clearTimeout(timer);
  }, [isLoading]);

  // Load event data - automatically load the single event from the flow (or use initialFlowData if provided)
  React.useEffect(() => {
    const loadEventData = async () => {
      setIsLoading(true);
      setContentReady(false);
      setContentVisible(false);

      if (!flowId) {
        setIsLoading(false);
        return;
      }

      try {
        // Use initialFlowData if provided (from FlowEditRouter), otherwise fetch from database
        let flowDataResult;
        if (initialFlowData && initialFlowData.events) {
          flowDataResult = JSON.parse(JSON.stringify(initialFlowData));
        } else {
          const result = await dispatch(fetchFlowWithDetailsAsync(flowId)).unwrap();
          flowDataResult = JSON.parse(JSON.stringify(result));
        }

        if (flowDataResult) {
          // Extract and store flow-level metadata
          const flowMetadata = {
            flow_name: flowDataResult.flow_name || 'Single Event',
            flow_description: flowDataResult.flow_description || '',
            test_case_id: flowDataResult.test_case_id || '',
          };
          setFlowData(flowMetadata);
          setOriginalFlowData(JSON.parse(JSON.stringify(flowMetadata)));

          if (flowDataResult.events && flowDataResult.events.length > 0) {
            // For single event flows, just load the first (and only) event
            const event = flowDataResult.events[0];
            const eventCopy = JSON.parse(JSON.stringify(event));
            setCurrentEvent(eventCopy);
            const savedEvent = event.event_db_id ? JSON.parse(JSON.stringify(event)) : null;
            setOriginalEvent(savedEvent);
            setHasUnsavedChanges(event.isNew || !savedEvent);
          } else {
            // Flow exists but has no events - create a new event
            const newEvent = {
              event_id: generateTempScenarioId(), // Temporary ID
              event_name: 'Single Event',
              event_order: 1,
              scenarios: [],
              isNew: true,
            };
            setCurrentEvent(newEvent);
            setOriginalEvent(null);
            setHasUnsavedChanges(true);
          }
        } else {
          console.error('Flow not found with ID:', flowId);
          alert(`Flow with ID "${flowId}" not found`);
          navigate('/flows');
        }
      } catch (error) {
        console.error('Error fetching flow from database:', error);
        alert(`Failed to load flow: ${error.message}`);
        navigate('/flows');
      }

      setIsLoading(false);
      setTimeout(() => {
        setContentReady(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setContentVisible(true);
          });
        });
      }, 100);
    };

    loadEventData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowId, dispatch, navigate]); // initialFlowData intentionally excluded - only load once

  // Load current market flows for validation
  React.useEffect(() => {
    const loadCurrentMarketFlows = async () => {
      try {
        const storedMarket = localStorage.getItem('market');
        const market = storedMarket ? JSON.parse(storedMarket) : currentMarket;

        if (!market?.id) {
          console.warn('No market selected');
          return;
        }

        const allFlows = await fetchAllFlows();
        const filtered = filterFlowsByMarket(allFlows, market.id);
        setCurrentMarketFlows(filtered);
      } catch (error) {
        console.error('Error loading current market flows:', error);
      }
    };

    loadCurrentMarketFlows();
  }, [currentMarket]);

  // Validate test_case_id for duplicates
  React.useEffect(() => {
    if (!flowData.test_case_id?.trim() || !originalFlowData) {
      setTestCaseIdError('');
      return;
    }

    // Check if test case ID exists in current market (excluding this flow)
    const currentFlowDbId = Number(flowId);
    const isDuplicate = currentMarketFlows.some(
      (flow) =>
        flow.test_case_id?.toLowerCase() === flowData.test_case_id.trim().toLowerCase() &&
        flow.flow_db_id !== currentFlowDbId &&
        !isNaN(currentFlowDbId), // Safety check
    );

    if (isDuplicate) {
      setTestCaseIdError(
        'This test case ID already exists in your market. Please choose a different ID.',
      );
    } else {
      setTestCaseIdError('');
    }
  }, [flowData.test_case_id, currentMarketFlows, flowId, originalFlowData]);

  // Track changes (both flow-level and event-level)
  React.useEffect(() => {
    if (!currentEvent.event_id || isDiscarding) return;

    let nextHasChanges = false;

    // Check for flow-level changes
    if (originalFlowData) {
      const hasFlowChanges =
        flowData.flow_description !== originalFlowData.flow_description ||
        flowData.test_case_id !== originalFlowData.test_case_id;

      if (hasFlowChanges) {
        nextHasChanges = true;
      }
    }

    // Check for event-level changes
    if (currentEvent.isNew) {
      nextHasChanges = true;
    } else if (originalEvent) {
      const changes = categorizeEventChanges(currentEvent, originalEvent);
      if (hasChanges(changes)) {
        nextHasChanges = true;
      }
    }

    setHasUnsavedChanges(nextHasChanges);
  }, [currentEvent, originalEvent, flowData, originalFlowData, flowId, isDiscarding]);

  // Browser refresh warning
  React.useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const handleNavigation = (navigationFn) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(() => navigationFn);
      setShowUnsavedDialog(true);
    } else {
      navigationFn();
    }
  };

  const handleConfirmNavigation = () => {
    setIsDiscarding(true);
    setShowUnsavedDialog(false);
    setHasUnsavedChanges(false);

    setTimeout(() => {
      if (pendingNavigation) {
        pendingNavigation();
        setPendingNavigation(null);
      }
      setIsDiscarding(false);
    }, 50);
  };

  const handleCancelNavigation = () => {
    setShowUnsavedDialog(false);
    setPendingNavigation(null);
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  };

  const handleSaveEvent = async (andNavigate = false) => {
    try {
      if (!currentEvent.event_id) {
        alert('Error: Event ID is missing. Cannot save.');
        return;
      }

      if (!currentEvent.event_name.trim()) {
        alert('Please enter an event name');
        return;
      }

      // Validate flow-level fields
      if (testCaseIdError) {
        alert('Please fix validation errors before saving:\n' + testCaseIdError);
        return;
      }

      // Require test_case_id to be filled
      if (!flowData.test_case_id?.trim()) {
        alert('Test Case ID is required. Please enter a unique identifier for this test case.');
        return;
      }

      // Validate attributes
      const hasEmptyAttributes = currentEvent.scenarios.some((scenario) =>
        scenario.scenario_attributes?.some(
          (attr) =>
            !attr.is_confluence_validation_schema_value &&
            (!attr.attribute_name?.trim() || !attr.expected_value?.trim()),
        ),
      );

      if (hasEmptyAttributes) {
        alert(
          'Cannot save: All attributes must have both a name and an expected value. Please complete all attribute fields or remove empty attributes.',
        );
        return;
      }

      // Check if any scenarios are missing matching attributes
      const scenariosWithoutMatching = currentEvent.scenarios.filter(
        (scenario) =>
          !scenario.matching_attribute_name || !scenario.matching_attribute_value?.trim(),
      );

      if (scenariosWithoutMatching.length > 0) {
        const confirmed = window.confirm(
          `Warning: ${scenariosWithoutMatching.length} scenario(s) do not have match key and match value set.\n\n` +
            `Scenarios without match key/value:\n${scenariosWithoutMatching.map((s) => `- ${s.scenario_name}`).join('\n')}\n\n` +
            `Do you want to continue saving without match key/value?`,
        );
        if (!confirmed) {
          return;
        }
      }

      const eventToSave = {
        ...currentEvent,
        event_name: currentEvent.event_name.trim(),
        isNew: false,
      };

      const changes = categorizeEventChanges(eventToSave, originalEvent);
      const isNewEvent = !currentEvent.event_db_id;
      const flowDbId = flowId;

      if (!flowDbId) {
        console.error('No flow database ID available.');
        alert('This flow needs to be saved to the database first before adding events.');
        return;
      }

      const eventData = {
        name: eventToSave.event_name,
        description: '',
        order: eventToSave.event_order,
      };

      const scenarios = eventToSave.scenarios.map((scenario) => ({
        name: scenario.scenario_name,
        type: scenario.scenario_type,
        description: '',
        matching_attribute_name: scenario.matching_attribute_name || null,
        matching_attribute_value: scenario.matching_attribute_value || null,
      }));

      const attributes = [];
      eventToSave.scenarios.forEach((scenario, scenarioIndex) => {
        scenario.scenario_attributes?.forEach((attr) => {
          // Schema-linked attributes must not be sent to insert-test-flow
          if (attr.is_confluence_validation_schema_value) return;
          attributes.push({
            scenarioIndex: scenarioIndex,
            name: attr.attribute_name,
            expectedValue: attr.expected_value,
            type: attr.attribute_type || 'Static',
          });
        });
      });

      // Save flow-level changes first
      if (originalFlowData) {
        const flowChanges = {};
        if (flowData.flow_description !== originalFlowData.flow_description) {
          flowChanges.flow_description = flowData.flow_description;
        }
        if (flowData.test_case_id !== originalFlowData.test_case_id) {
          flowChanges.test_case_id = flowData.test_case_id;
        }

        if (Object.keys(flowChanges).length > 0) {
          const flowUpdateResult = await dispatch(updateTestFlowAsync(flowDbId, flowChanges));

          if (!flowUpdateResult.success) {
            console.error('Failed to update flow in database:', flowUpdateResult.error);
            alert(`Warning: Could not save flow changes to database (${flowUpdateResult.error})`);
          } else {
            // Update original flow data after successful save
            setOriginalFlowData(JSON.parse(JSON.stringify(flowData)));
          }
        }
      }

      setShowProgressDialog(true);

      let result;

      if (isNewEvent) {
        result = await dispatch(
          createTestEventWithScenariosAsync(flowDbId, eventData, scenarios, attributes),
        );

        if (result.success && result.eventId) {
          eventToSave.event_db_id = result.eventId;

          if (result.scenarioIds && result.scenarioIds.length > 0) {
            const inboundScenarioIds = result.scenarioIds
              .filter((s) => s.type === 'inbound')
              .map((s) => s.id);
            const outboundScenarioIds = result.scenarioIds
              .filter((s) => s.type !== 'inbound')
              .map((s) => s.id);

            let inboundIndex = 0;
            let outboundIndex = 0;

            eventToSave.scenarios = eventToSave.scenarios.map((scenario) => {
              if (
                scenario.scenario_type === 'inbound' &&
                inboundIndex < inboundScenarioIds.length
              ) {
                return { ...scenario, scenario_db_id: inboundScenarioIds[inboundIndex++] };
              } else if (
                scenario.scenario_type !== 'inbound' &&
                outboundIndex < outboundScenarioIds.length
              ) {
                return { ...scenario, scenario_db_id: outboundScenarioIds[outboundIndex++] };
              }
              return scenario;
            });
          }

          if (result.attributeIds) {
            let inboundAttrIndex = 0;
            let outboundAttrIndex = 0;

            eventToSave.scenarios = eventToSave.scenarios.map((scenario) => {
              if (scenario.scenario_attributes && scenario.scenario_attributes.length > 0) {
                const updatedAttributes = scenario.scenario_attributes.map((attr) => {
                  if (
                    scenario.scenario_type === 'inbound' &&
                    result.attributeIds.inbound &&
                    inboundAttrIndex < result.attributeIds.inbound.length
                  ) {
                    return {
                      ...attr,
                      attribute_db_id: result.attributeIds.inbound[inboundAttrIndex++],
                    };
                  } else if (
                    scenario.scenario_type !== 'inbound' &&
                    result.attributeIds.outbound &&
                    outboundAttrIndex < result.attributeIds.outbound.length
                  ) {
                    return {
                      ...attr,
                      attribute_db_id: result.attributeIds.outbound[outboundAttrIndex++],
                    };
                  }
                  return attr;
                });
                return { ...scenario, scenario_attributes: updatedAttributes };
              }
              return scenario;
            });
          }
        }
      } else {
        const updateResult = await dispatch(updateTestEventAsync(changes));

        if (changes.scenarios.create.length > 0 || changes.attributes.create.length > 0) {
          if (changes.scenarios.create.length > 0) {
            const newScenarios = changes.scenarios.create.map((scenario) => ({
              name: scenario.scenario_name,
              type: scenario.scenario_type,
              description: scenario.description || '',
              matching_attribute_name: scenario.matching_attribute_name || null,
              matching_attribute_value: scenario.matching_attribute_value || null,
            }));

            const newScenarioAttributes = [];
            changes.scenarios.create.forEach((scenario, scenarioIndex) => {
              (scenario.scenario_attributes || []).forEach((attr) => {
                // Schema-linked attributes must not be sent to insert-test-flow
                if (attr.is_confluence_validation_schema_value) return;
                newScenarioAttributes.push({
                  scenarioIndex,
                  name: attr.attribute_name,
                  expectedValue: attr.expected_value,
                  type: attr.attribute_type || 'Static',
                });
              });
            });

            const createResult = await dispatch(
              createTestEventWithScenariosAsync(
                flowDbId,
                eventData,
                newScenarios,
                newScenarioAttributes,
                eventToSave.event_db_id,
              ),
            );

            if (!createResult.success) {
              console.error('Failed to create new items:', createResult.error);
            } else if (createResult.scenarioIds || createResult.attributeIds) {
              let newScenarioIndex = 0;
              eventToSave.scenarios = eventToSave.scenarios.map((scenario) => {
                if (
                  !scenario.scenario_db_id &&
                  createResult.scenarioIds &&
                  newScenarioIndex < createResult.scenarioIds.length
                ) {
                  const scenarioIdInfo = createResult.scenarioIds[newScenarioIndex++];
                  return { ...scenario, scenario_db_id: scenarioIdInfo.id };
                }
                return scenario;
              });

              if (createResult.attributeIds) {
                let inboundAttrIndex = 0;
                let outboundAttrIndex = 0;

                eventToSave.scenarios = eventToSave.scenarios.map((scenario) => {
                  if (scenario.scenario_attributes && scenario.scenario_attributes.length > 0) {
                    const updatedAttributes = scenario.scenario_attributes.map((attr) => {
                      if (!attr.attribute_db_id) {
                        if (
                          scenario.scenario_type === 'inbound' &&
                          createResult.attributeIds.inbound &&
                          inboundAttrIndex < createResult.attributeIds.inbound.length
                        ) {
                          return {
                            ...attr,
                            attribute_db_id: createResult.attributeIds.inbound[inboundAttrIndex++],
                          };
                        } else if (
                          scenario.scenario_type !== 'inbound' &&
                          createResult.attributeIds.outbound &&
                          outboundAttrIndex < createResult.attributeIds.outbound.length
                        ) {
                          return {
                            ...attr,
                            attribute_db_id:
                              createResult.attributeIds.outbound[outboundAttrIndex++],
                          };
                        }
                      }
                      return attr;
                    });
                    return { ...scenario, scenario_attributes: updatedAttributes };
                  }
                  return scenario;
                });
              }
            }
          }

          const newAttributesOnExistingScenarios = changes.attributes.create.filter(
            (attr) => attr.scenario_db_id,
          );
          if (newAttributesOnExistingScenarios.length > 0) {
            const attrResult = await dispatch(
              createAttributesOnExistingScenariosAsync(newAttributesOnExistingScenarios),
            );

            if (attrResult.success && attrResult.attributeIds) {
              let inboundAttrIndex = 0;
              let outboundAttrIndex = 0;

              eventToSave.scenarios = eventToSave.scenarios.map((scenario) => {
                if (scenario.scenario_db_id && scenario.scenario_attributes) {
                  const updatedAttributes = scenario.scenario_attributes.map((attr) => {
                    if (!attr.attribute_db_id) {
                      if (
                        scenario.scenario_type === 'inbound' &&
                        attrResult.attributeIds.inboundIds &&
                        inboundAttrIndex < attrResult.attributeIds.inboundIds.length
                      ) {
                        return {
                          ...attr,
                          attribute_db_id: attrResult.attributeIds.inboundIds[inboundAttrIndex++],
                        };
                      } else if (
                        scenario.scenario_type !== 'inbound' &&
                        attrResult.attributeIds.outboundIds &&
                        outboundAttrIndex < attrResult.attributeIds.outboundIds.length
                      ) {
                        return {
                          ...attr,
                          attribute_db_id: attrResult.attributeIds.outboundIds[outboundAttrIndex++],
                        };
                      }
                    }
                    return attr;
                  });
                  return { ...scenario, scenario_attributes: updatedAttributes };
                }
                return scenario;
              });
            } else if (!attrResult.success) {
              console.error('Failed to create attributes on existing scenarios:', attrResult.error);
            }
          }
        }

        if (changes.scenarios.delete.length > 0 || changes.attributes.delete.length > 0) {
          dispatch(setProgress('deleting'));

          const deletePromises = [];

          if (changes.attributes.delete.length > 0) {
            changes.attributes.delete.forEach((attr) => {
              if (attr.attribute_db_id) {
                deletePromises.push(
                  dispatch(deleteAttributeAsync(attr.attribute_db_id, attr.scenario_type))
                    .then((result) => ({
                      type: 'DELETE_ATTRIBUTE',
                      attributeId: attr.attribute_db_id,
                      success: result.success,
                      error: result.error,
                    }))
                    .catch((error) => ({
                      type: 'DELETE_ATTRIBUTE',
                      attributeId: attr.attribute_db_id,
                      success: false,
                      error: error.message,
                    })),
                );
              }
            });
          }

          if (deletePromises.length > 0) {
            const attributeDeleteResults = await Promise.all(deletePromises);
            const attributeFailures = attributeDeleteResults.filter((r) => !r.success);
            if (attributeFailures.length > 0) {
              console.error('Some attribute delete operations failed:', attributeFailures);
            }
          }

          const scenarioDeletePromises = [];
          if (changes.scenarios.delete.length > 0) {
            changes.scenarios.delete.forEach((scenario) => {
              if (scenario.scenario_db_id) {
                scenarioDeletePromises.push(
                  dispatch(deleteScenarioAsync(scenario.scenario_db_id, scenario.scenario_type))
                    .then((result) => ({
                      type: 'DELETE_SCENARIO',
                      scenarioId: scenario.scenario_db_id,
                      success: result.success,
                      error: result.error,
                    }))
                    .catch((error) => ({
                      type: 'DELETE_SCENARIO',
                      scenarioId: scenario.scenario_db_id,
                      success: false,
                      error: error.message,
                    })),
                );
              }
            });
          }

          if (scenarioDeletePromises.length > 0) {
            const scenarioDeleteResults = await Promise.all(scenarioDeletePromises);
            const scenarioFailures = scenarioDeleteResults.filter((r) => !r.success);
            if (scenarioFailures.length > 0) {
              console.error('Some scenario delete operations failed:', scenarioFailures);
            }
          }
        }

        result = updateResult;
      }

      setTimeout(() => {
        setShowProgressDialog(false);
      }, 1000);

      if (!result || !result.success) {
        console.error('API save failed:', result?.error);
        alert(`Warning: Could not save to database (${result?.error}).`);
      }

      const savedEventCopy = JSON.parse(JSON.stringify(eventToSave));
      setCurrentEvent(savedEventCopy);
      setOriginalEvent(JSON.parse(JSON.stringify(savedEventCopy)));
      setHasUnsavedChanges(false);

      // Re-fetch flow details so saved dynamic schema values reflect back in UI
      if (result && result.success) {
        dispatch(fetchFlowWithDetailsAsync(flowId));
      }

      window.dispatchEvent(new Event('flowUpdated'));

      if (andNavigate) {
        setShowUnsavedDialog(false);
        setTimeout(() => {
          if (pendingNavigation) {
            pendingNavigation();
            setPendingNavigation(null);
          }
        }, 100);
      } else {
        alert('Event saved successfully!');
      }
    } catch (error) {
      console.error('Error saving event:', error);
      alert(`Error saving event: ${error.message}`);
    }
  };

  const handleBackToFlows = () => {
    handleNavigation(() => navigate('/flows'));
  };

  const toggleScenarioExpansion = (scenarioId) => {
    setExpandedScenarios((prev) => ({
      ...prev,
      [scenarioId]: !prev[scenarioId],
    }));
  };

  const handleAddScenarioType = () => {
    if (!newScenarioType.scenario_name.trim()) {
      alert('Please enter a scenario name');
      return;
    }

    const nextScenarioId = generateTempScenarioId();
    const newScenario = {
      scenario_id: nextScenarioId,
      scenario_name: newScenarioType.scenario_name.trim(),
      scenario_type: newScenarioType.scenario_type,
      scenario_attributes: [],
      matching_attribute_name: '',
      matching_attribute_value: '',
    };

    if (
      newScenarioType.scenario_type?.startsWith('outbound') &&
      newScenarioType.cloneFromInboundId
    ) {
      const source = (currentEvent.scenarios || []).find(
        (s) => s.scenario_id === newScenarioType.cloneFromInboundId,
      );
      if (source) {
        const clonedAttrs = (source.scenario_attributes || []).map((attr) => ({
          attribute_id: generateTempAttributeId(),
          attribute_name: attr.attribute_name || '',
          expected_value: attr.expected_value || '',
          attribute_type: attr.attribute_type || 'Static',
        }));

        newScenario.scenario_attributes = clonedAttrs;
        newScenario.matching_attribute_name = source.matching_attribute_name || '';
        newScenario.matching_attribute_value = source.matching_attribute_value || '';
        if (!newScenarioType.scenario_name.trim()) {
          newScenario.scenario_name = `${source.scenario_name} - clone`;
        }
      }
    }

    setCurrentEvent((prev) => ({
      ...prev,
      scenarios: [...(prev.scenarios || []), newScenario],
    }));

    setExpandedScenarios((prev) => ({
      ...prev,
      [newScenario.scenario_id]: true,
    }));

    setNewScenarioType({ scenario_name: '', scenario_type: 'inbound', cloneFromInboundId: '' });
    setAddScenarioModalOpen(false);
  };

  const handleDeleteScenario = (scenarioId) => {
    if (window.confirm('Are you sure you want to delete this scenario?')) {
      setCurrentEvent((prev) => ({
        ...prev,
        scenarios: prev.scenarios.filter((scenario) => scenario.scenario_id !== scenarioId),
      }));

      setExpandedScenarios((prev) => {
        const newState = { ...prev };
        delete newState[scenarioId];
        return newState;
      });
    }
  };

  const handleAddAttribute = (scenarioId) => {
    const nextAttributeId = generateTempAttributeId();
    const newAttribute = {
      attribute_id: nextAttributeId,
      attribute_name: '',
      expected_value: '',
      attribute_type: 'Static',
    };

    setCurrentEvent((prev) => ({
      ...prev,
      scenarios: prev.scenarios.map((scenario) =>
        scenario.scenario_id === scenarioId
          ? {
              ...scenario,
              scenario_attributes: [...(scenario.scenario_attributes || []), newAttribute],
            }
          : scenario,
      ),
    }));
  };

  const handleDeleteAttribute = (scenarioId, attributeId) => {
    if (window.confirm('Are you sure you want to delete this attribute?')) {
      setCurrentEvent((prev) => ({
        ...prev,
        scenarios: prev.scenarios.map((scenario) =>
          scenario.scenario_id === scenarioId
            ? {
                ...scenario,
                scenario_attributes: scenario.scenario_attributes.filter(
                  (attr) => attr.attribute_id !== attributeId,
                ),
              }
            : scenario,
        ),
      }));
    }
  };

  // Whitespace validation function
  const validateWhitespace = (event) => {
    const errors = new Set();
    const attributeErrors = {};
    const scenarioErrors = {};

    if (!event || !event.scenarios) {
      setValidationError('');
      setErrorLocations({ attribute: {}, scenario: {} });
      return;
    }

    event.scenarios.forEach((scenario) => {
      // Scenario Name — cannot start with whitespace
      if (scenario.scenario_name && /^\s/.test(scenario.scenario_name)) {
        scenarioErrors[scenario.scenario_id] = true;
        errors.add('Whitespace detected at beginning of Scenario Name');
      }

      // Match Key/Value Validation — both fields must be filled or both must be empty
      const hasMatchingName = Boolean(scenario.matching_attribute_name);
      const hasMatchingValue = Boolean(scenario.matching_attribute_value?.trim());

      if (hasMatchingName !== hasMatchingValue) {
        scenarioErrors[scenario.scenario_id] = true;
        errors.add('Match key requires both key and value');
      }

      // Match Value — cannot start with whitespace if filled
      if (hasMatchingValue && /^\s/.test(scenario.matching_attribute_value || '')) {
        scenarioErrors[scenario.scenario_id] = true;
        errors.add('Whitespace detected at beginning of Match Value');
      }

      // Attributes validation
      if (scenario.scenario_attributes && Array.isArray(scenario.scenario_attributes)) {
        scenario.scenario_attributes.forEach((attr) => {
          // Attribute Name — cannot contain ANY whitespace
          if (attr.attribute_name && /\s/.test(attr.attribute_name)) {
            if (!attributeErrors[attr.attribute_id]) {
              attributeErrors[attr.attribute_id] = {};
            }
            attributeErrors[attr.attribute_id].name = true;
            errors.add('Whitespace detected in Attribute Name');
          }

          // Expected Value — cannot start with whitespace
          if (attr.expected_value && /^\s/.test(attr.expected_value)) {
            if (!attributeErrors[attr.attribute_id]) {
              attributeErrors[attr.attribute_id] = {};
            }
            attributeErrors[attr.attribute_id].value = true;
            errors.add('Whitespace detected at beginning of Expected Value');
          }
        });
      }
    });

    if (errors.size > 0) {
      setValidationError(Array.from(errors).join('. '));
      setErrorLocations({ attribute: attributeErrors, scenario: scenarioErrors });
    } else {
      setValidationError('');
      setErrorLocations({ attribute: {}, scenario: {} });
    }
  };

  const handleAttributeChange = (scenarioId, attributeId, field, value) => {
    setCurrentEvent((prev) => {
      const updated = {
        ...prev,
        scenarios: prev.scenarios.map((scenario) =>
          scenario.scenario_id === scenarioId
            ? {
                ...scenario,
                scenario_attributes: scenario.scenario_attributes.map((attr) =>
                  attr.attribute_id === attributeId ? { ...attr, [field]: value } : attr,
                ),
              }
            : scenario,
        ),
      };
      validateWhitespace(updated);
      return updated;
    });
  };

  const handleScenarioNameChange = (scenarioId, newName) => {
    setCurrentEvent((prev) => {
      const updated = {
        ...prev,
        scenarios: prev.scenarios.map((scenario) =>
          scenario.scenario_id === scenarioId ? { ...scenario, scenario_name: newName } : scenario,
        ),
      };
      validateWhitespace(updated);
      return updated;
    });
  };

  const handleMatchingAttributeChange = (scenarioId, field, value) => {
    setCurrentEvent((prev) => {
      const updated = {
        ...prev,
        scenarios: prev.scenarios.map((scenario) =>
          scenario.scenario_id === scenarioId ? { ...scenario, [field]: value } : scenario,
        ),
      };
      validateWhitespace(updated);
      return updated;
    });
  };

  const getScenarioTypeConfig = (scenarioType) => {
    return SCENARIO_TYPES.find((type) => type.value === scenarioType) || SCENARIO_TYPES[0];
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          p: 3,
          minHeight: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: showSpinner ? 1 : 0,
          transition: 'opacity 0.2s ease-in-out',
        }}
      >
        <CircularProgress
          size="4rem"
          sx={{
            color: 'secondary.main',
          }}
        />
      </Box>
    );
  }

  if (!contentReady) {
    return null;
  }

  // Locked when any scenario in this event is linked to a confluence schema.
  const schemaLocked = (currentEvent?.scenarios || []).some(
    (s) => s.confluence_validation_schema_id != null,
  );

  return (
    <Box
      sx={{
        p: 3,
        opacity: contentVisible ? 1 : 0,
        transition: 'opacity 0.4s ease-in-out',
        willChange: 'opacity',
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={handleBackToFlows} sx={{ mr: 1, color: 'primary.main' }}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h4" component="h1">
              Single Event Test
            </Typography>
            {hasUnsavedChanges && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <WarningIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 500 }}>
                  {currentEvent.isNew ? 'New event' : 'Unsaved changes'}
                </Typography>
              </Box>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Flow ID: {flowId}
          </Typography>
        </Box>
      </Box>

      {/* Unsaved changes alert */}
      {hasUnsavedChanges && (
        <Alert severity={currentEvent.isNew ? 'info' : 'warning'} sx={{ mb: 3 }}>
          {currentEvent.isNew
            ? "This is a new event. Click 'Save Event' to add it to the flow permanently."
            : 'You have unsaved changes. Remember to save your work before leaving this page.'}
        </Alert>
      )}

      {/* Test Case Details */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Test Case Details
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Flow Name"
            value={flowData.flow_name}
            disabled
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'custom.surfaceAlt',
                '&.Mui-disabled': {
                  backgroundColor: 'custom.surfaceAlt',
                },
              },
            }}
          />
          <TextField
            label="Description"
            value={flowData.flow_description}
            onChange={(e) => setFlowData((prev) => ({ ...prev, flow_description: e.target.value }))}
            fullWidth
            multiline
            rows={3}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'text.secondary',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'primary.main',
              },
            }}
          />
          <TextField
            label="Test Case ID"
            value={flowData.test_case_id}
            onChange={(e) => setFlowData((prev) => ({ ...prev, test_case_id: e.target.value }))}
            fullWidth
            required
            error={!!testCaseIdError}
            helperText={testCaseIdError || 'Required - Unique identifier for this test case'}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'text.secondary',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'primary.main',
              },
            }}
          />
        </Box>
      </Paper>

      {/* Event Details Form */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Event Details
        </Typography>

        {/* Validation Error Alert */}
        {validationError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {validationError}
          </Alert>
        )}

        <TextField
          label="Event Name"
          value={currentEvent.event_name}
          onChange={(e) => setCurrentEvent((prev) => ({ ...prev, event_name: e.target.value }))}
          disabled={schemaLocked}
          fullWidth
          required
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: 'text.secondary',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
              },
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: 'primary.main',
            },
          }}
        />
      </Paper>

      {/* Scenario Types Section */}
      <Paper sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">Scenarios ({currentEvent.scenarios?.length || 0})</Typography>
          </Box>
          {!schemaLocked && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddScenarioModalOpen(true)}
              sx={{
                bgcolor: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.light',
                },
              }}
            >
              Add Scenario
            </Button>
          )}
        </Box>

        {!currentEvent.scenarios || currentEvent.scenarios.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <Typography>No scenarios yet. Add your first scenario to get started.</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {currentEvent.scenarios.map((scenario) => {
              const typeConfig = getScenarioTypeConfig(scenario.scenario_type);
              const isExpanded = expandedScenarios[scenario.scenario_id];

              return (
                <Card key={scenario.scenario_id}>
                  <CardContent sx={{ pb: 1 }}>
                    {/* Scenario Header */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        mb: 1,
                      }}
                      onClick={() => toggleScenarioExpansion(scenario.scenario_id)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                          value={scenario.scenario_name}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleScenarioNameChange(scenario.scenario_id, e.target.value);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          disabled={schemaLocked}
                          size="small"
                          variant="standard"
                          error={errorLocations.scenario[scenario.scenario_id]}
                          sx={{
                            '& .MuiInput-root': {
                              fontSize: '1rem',
                              fontWeight: 500,
                            },
                            '& .MuiInput-underline:before': {
                              borderBottom: (theme) =>
                                errorLocations.scenario[scenario.scenario_id]
                                  ? `2px solid ${theme.palette.error.main}`
                                  : `1px dashed ${theme.palette.divider}`,
                            },
                            '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                              borderBottom: (theme) =>
                                errorLocations.scenario[scenario.scenario_id]
                                  ? `2px solid ${theme.palette.error.main}`
                                  : `1px solid ${theme.palette.text.primary}`,
                            },
                            '& .MuiInput-underline:after': {
                              borderBottomColor: errorLocations.scenario[scenario.scenario_id]
                                ? 'error.main'
                                : 'primary.main',
                            },
                            minWidth: '150px',
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          ID: {scenario.scenario_db_id || `${scenario.scenario_id} (temp)`}
                        </Typography>
                        <Box
                          sx={{
                            backgroundColor: 'transparent',
                            color: typeConfig.color,
                            border: `1px solid ${typeConfig.borderColor}`,
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            display: 'inline-flex',
                            alignItems: 'center',
                          }}
                        >
                          {typeConfig.label}
                        </Box>

                        {/* Match Key and Match Value Fields */}
                        <FormControl
                          size="small"
                          sx={{ minWidth: 150 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Select
                            value={scenario.matching_attribute_name || ''}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleMatchingAttributeChange(
                                scenario.scenario_id,
                                'matching_attribute_name',
                                e.target.value,
                              );
                            }}
                            disabled={schemaLocked}
                            displayEmpty
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '&:hover fieldset': { borderColor: 'text.secondary' },
                                '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                              },
                            }}
                          >
                            <MenuItem value="">
                              <em>Match Key</em>
                            </MenuItem>
                            <MenuItem value="beacon.id">beacon.id</MenuItem>
                            <MenuItem value="link_name">link_name</MenuItem>
                            <MenuItem value="legacy_beacon_id">legacy_beacon_id</MenuItem>
                          </Select>
                        </FormControl>

                        <TextField
                          value={scenario.matching_attribute_value || ''}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleMatchingAttributeChange(
                              scenario.scenario_id,
                              'matching_attribute_value',
                              e.target.value,
                            );
                          }}
                          onClick={(e) => e.stopPropagation()}
                          disabled={schemaLocked}
                          placeholder="Match value"
                          size="small"
                          error={errorLocations.scenario[scenario.scenario_id]}
                          sx={{
                            minWidth: 150,
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: errorLocations.scenario[scenario.scenario_id]
                                  ? 'error.main'
                                  : 'divider',
                              },
                              '&:hover fieldset': {
                                borderColor: errorLocations.scenario[scenario.scenario_id]
                                  ? 'error.main'
                                  : 'text.primary',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: errorLocations.scenario[scenario.scenario_id]
                                  ? 'error.main'
                                  : 'primary.main',
                              },
                            },
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {!schemaLocked && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteScenario(scenario.scenario_id);
                            }}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                      </Box>
                    </Box>

                    {/* Collapsible Attributes Table */}
                    <Collapse in={isExpanded}>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Attributes ({scenario.scenario_attributes?.length || 0})
                        </Typography>

                        {!scenario.scenario_attributes ||
                        scenario.scenario_attributes.length === 0 ? (
                          <Box
                            sx={{
                              textAlign: 'center',
                              py: 2,
                              color: 'text.secondary',
                            }}
                          >
                            <Typography variant="body2">No attributes yet</Typography>
                          </Box>
                        ) : (
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Attribute Name</TableCell>
                                  <TableCell>Expected Value</TableCell>
                                  <TableCell>Type</TableCell>
                                  <TableCell width={100}>Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {scenario.scenario_attributes.map((attribute) => (
                                  <TableRow key={attribute.attribute_id}>
                                    <TableCell>
                                      <TextField
                                        size="small"
                                        value={attribute.attribute_name}
                                        onChange={(e) =>
                                          handleAttributeChange(
                                            scenario.scenario_id,
                                            attribute.attribute_id,
                                            'attribute_name',
                                            e.target.value,
                                          )
                                        }
                                        disabled={attribute.is_confluence_validation_schema_value}
                                        placeholder="e.g. event.customAttribute.url"
                                        fullWidth
                                        error={
                                          errorLocations.attribute[attribute.attribute_id]?.name
                                        }
                                        sx={{
                                          '& .MuiOutlinedInput-root': {
                                            '&:hover fieldset': {
                                              borderColor: errorLocations.attribute[
                                                attribute.attribute_id
                                              ]?.name
                                                ? 'error.main'
                                                : 'text.secondary',
                                            },
                                            '&.Mui-focused fieldset': {
                                              borderColor: errorLocations.attribute[
                                                attribute.attribute_id
                                              ]?.name
                                                ? 'error.main'
                                                : 'primary.main',
                                            },
                                          },
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <TextField
                                        size="small"
                                        value={attribute.expected_value}
                                        onChange={(e) =>
                                          handleAttributeChange(
                                            scenario.scenario_id,
                                            attribute.attribute_id,
                                            'expected_value',
                                            e.target.value,
                                          )
                                        }
                                        disabled={
                                          attribute.is_confluence_validation_schema_value &&
                                          attribute.attribute_type === 'Static'
                                        }
                                        placeholder="Enter expected value"
                                        fullWidth
                                        error={
                                          errorLocations.attribute[attribute.attribute_id]?.value
                                        }
                                        sx={{
                                          '& .MuiOutlinedInput-root': {
                                            '&:hover fieldset': {
                                              borderColor: errorLocations.attribute[
                                                attribute.attribute_id
                                              ]?.value
                                                ? 'error.main'
                                                : 'text.secondary',
                                            },
                                            '&.Mui-focused fieldset': {
                                              borderColor: errorLocations.attribute[
                                                attribute.attribute_id
                                              ]?.value
                                                ? 'error.main'
                                                : 'primary.main',
                                            },
                                          },
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <FormControl size="small" fullWidth>
                                        <Select
                                          value={attribute.attribute_type}
                                          onChange={(e) =>
                                            handleAttributeChange(
                                              scenario.scenario_id,
                                              attribute.attribute_id,
                                              'attribute_type',
                                              e.target.value,
                                            )
                                          }
                                          disabled={attribute.is_confluence_validation_schema_value}
                                          sx={{
                                            '& .MuiOutlinedInput-root': {
                                              '&:hover fieldset': {
                                                borderColor: 'text.secondary',
                                              },
                                              '&.Mui-focused fieldset': {
                                                borderColor: 'primary.main',
                                              },
                                            },
                                          }}
                                        >
                                          <MenuItem value="Static">Static</MenuItem>
                                          <MenuItem value="Dynamic">Dynamic</MenuItem>
                                        </Select>
                                      </FormControl>
                                    </TableCell>
                                    <TableCell>
                                      {!schemaLocked && (
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handleDeleteAttribute(
                                              scenario.scenario_id,
                                              attribute.attribute_id,
                                            )
                                          }
                                          color="error"
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}

                        {!schemaLocked && (
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => handleAddAttribute(scenario.scenario_id)}
                            sx={{
                              mt: 1,
                              color: 'primary.main',
                              '&:hover': {
                                backgroundColor: 'action.hover',
                              },
                            }}
                          >
                            Add Attribute
                          </Button>
                        )}
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
      </Paper>

      {/* Save/Cancel Actions */}
      <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={handleBackToFlows}
          sx={{
            borderColor: 'text.secondary',
            color: 'text.secondary',
            '&:hover': {
              borderColor: 'text.secondary',
              backgroundColor: 'action.hover',
            },
          }}
        >
          Back to Flows
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveEvent}
          disabled={!hasUnsavedChanges || !currentEvent.event_name.trim() || validationError}
          sx={{
            bgcolor: 'primary.main',
            '&:hover': {
              bgcolor: 'primary.light',
            },
            '&:disabled': {
              bgcolor: 'action.disabledBackground',
            },
          }}
        >
          {hasUnsavedChanges ? 'Save Event' : 'Saved'}
        </Button>
      </Box>

      {/* Add Scenario Type Modal */}
      <Dialog
        open={addScenarioModalOpen}
        onClose={() => setAddScenarioModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Scenario</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Scenario Name"
              value={newScenarioType.scenario_name}
              onChange={(e) =>
                setNewScenarioType((prev) => ({
                  ...prev,
                  scenario_name: e.target.value,
                }))
              }
              placeholder="e.g. Button Click Event"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'text.secondary',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: 'primary.main',
                },
              }}
            />
            <FormControl
              fullWidth
              sx={{
                '& .MuiInputLabel-root.Mui-focused': {
                  color: 'primary.main',
                },
              }}
            >
              <InputLabel>Scenario Type</InputLabel>
              <Select
                value={newScenarioType.scenario_type}
                label="Scenario Type"
                onChange={(e) =>
                  setNewScenarioType((prev) => ({
                    ...prev,
                    scenario_type: e.target.value,
                  }))
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'text.secondary',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              >
                {SCENARIO_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        width: '100%',
                      }}
                    >
                      <span>{type.displayName}</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {newScenarioType.scenario_type?.startsWith('outbound') && (
              <FormControl
                fullWidth
                sx={{
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: 'primary.main',
                  },
                }}
              >
                <InputLabel>Clone from Inbound Scenario</InputLabel>
                <Select
                  value={newScenarioType.cloneFromInboundId || ''}
                  label="Clone from Inbound Scenario"
                  onChange={(e) =>
                    setNewScenarioType((prev) => ({
                      ...prev,
                      cloneFromInboundId: e.target.value,
                    }))
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: 'text.secondary',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {(currentEvent.scenarios || [])
                    .filter((s) => s.scenario_type === 'inbound')
                    .map((s) => (
                      <MenuItem key={s.scenario_id} value={s.scenario_id}>
                        {s.scenario_name}
                        {s.scenario_db_id ? ` (ID:${s.scenario_db_id})` : ''}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAddScenarioModalOpen(false)}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddScenarioType}
            sx={{
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.light',
              },
            }}
          >
            Create Scenario Type
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unsaved Changes Dialog */}
      <Dialog open={showUnsavedDialog} onClose={handleCancelNavigation}>
        <DialogTitle>{currentEvent.isNew ? 'Discard New Event?' : 'Unsaved Changes'}</DialogTitle>
        <DialogContent>
          <Typography>
            {currentEvent.isNew
              ? "This new event hasn't been saved yet and will be discarded. Are you sure you want to leave?"
              : 'You have unsaved changes that will be lost. Are you sure you want to leave without saving?'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelNavigation}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            Stay
          </Button>
          <Button
            onClick={() => handleSaveEvent(true)}
            variant="contained"
            sx={{
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.light',
              },
            }}
          >
            Save & Continue
          </Button>
          <Button onClick={handleConfirmNavigation} color="error">
            {currentEvent.isNew ? 'Discard Event' : 'Discard Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Progress Dialog */}
      <SaveProgressDialog open={showProgressDialog} progress={saveProgress} />
    </Box>
  );
}
